import { useId } from "react";
import type {
  BuyerSequenceListingOptionDto,
  BuyerSequenceSelection,
} from "@/api/buyerSequence";
import { Select } from "@/components/ui/Select";

/** The option value that stands for "No listing (internal referral)". */
const NO_LISTING = "__no-listing__";
/** The empty placeholder — only reachable while nothing is selected (G3). */
const UNSELECTED = "";

export const NO_LISTING_LABEL = "No listing (internal referral)";
export const PICK_LISTING_PLACEHOLDER = "Pick the associated listing…";

/**
 * "Message about" (G3) — which of the buyer's associated listings the messages
 * talk about, or none.
 *
 * <p>The SERVER resolves the first selection: one associated listing is
 * preselected, none reads "No listing", and two or more leave it unselected so
 * the specialist has to choose. From the first pick on, the tab sends what was
 * picked. The placeholder is offered only while it is the current value — once
 * a listing or "No listing" is chosen, going back to "nothing" is not a state
 * the messages can be written in.</p>
 */
export function MessageAboutSelect({
  listings,
  value,
  onChange,
  disabled,
}: {
  listings: BuyerSequenceListingOptionDto[];
  /** The resolved selection: a listing id, "none", or null for unselected. */
  value: { listingId: string } | "none" | null;
  onChange: (next: BuyerSequenceSelection) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const selectValue =
    value === null ? UNSELECTED : value === "none" ? NO_LISTING : value.listingId;

  return (
    <div className="grid gap-space-1">
      <label htmlFor={id} className="text-caption uppercase tracking-wide text-gray-mid">
        Message about
      </label>
      <Select
        id={id}
        value={selectValue}
        disabled={disabled}
        data-testid="outreach-message-about"
        onChange={(e) => {
          const v = e.target.value;
          if (v === UNSELECTED) return;
          onChange(v === NO_LISTING ? { noListing: true } : { listingId: v });
        }}
      >
        {value === null && (
          <option value={UNSELECTED} disabled>
            {PICK_LISTING_PLACEHOLDER}
          </option>
        )}
        {listings.map((l) => (
          <option key={l.listingId} value={l.listingId}>
            {l.label}
          </option>
        ))}
        <option value={NO_LISTING}>{NO_LISTING_LABEL}</option>
      </Select>
    </div>
  );
}
